import { Injectable, Signal, signal } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { hashObject } from '@app/utils/crypto/crypto.utils';
import { createDate } from '@app/utils/datetime/datetime.utils';
import { RimsEnumValue, RimsStepPropertyTree, RimsStepPropertySchema } from '@app/models/rims.model';
import { getPropertyType } from '@app/utils/rims/rims.utils';

export type RimsFormDescriptorField = {
  name: string;
  displayName?: string;
  description?: string | null;
  parentGroup: RimsStepPropertyTree | null;
  placeholder?: string;
  value?: string | Record<string, unknown>;
  fieldType:
    'input-text' | 'input-number' | 'datepicker' | 'checkbox' | 'dropdown' | 'form-redirect' | 'text' | 'unknown';
  rawFieldData: RimsStepPropertyTree;
  options?: RimsEnumValue[];
};

export type RimsFormDescriptor = {
  formHash: string;
  formName: string;
  formPath: string;
  formGroup: FormGroup;
  formRawData: RimsStepPropertyTree;
  renderFields?: RimsFormDescriptorField[];
};

@Injectable({
  providedIn: 'root',
})
export class RimsFormBuilderService {
  private formCollectionsSignal = signal<Map<string, RimsFormDescriptor>>(new Map([]));
  private formTracker = new WeakMap();
  private currentForm = signal<RimsFormDescriptor | null>(null);

  public get activeForm(): Signal<RimsFormDescriptor | null> {
    return this.currentForm;
  }

  public deregisterAllForms(): void {
    this.formCollectionsSignal.update((collection) => {
      Array.from(collection.entries()).forEach(([key, formDesc]) => {
        collection.delete(key);
        this.formTracker.delete(formDesc);
      });

      return collection;
    });

    this.currentForm.update(() => null);
  }

  public async buildForm(rimsStepPropertyTree: RimsStepPropertyTree): Promise<RimsFormDescriptor> {
    const formDescriptor: RimsFormDescriptor = {
      formHash: await hashObject(rimsStepPropertyTree),
      formName: rimsStepPropertyTree.name,
      formPath: rimsStepPropertyTree.objectPath,
      formGroup: new FormGroup([]),
      formRawData: rimsStepPropertyTree,
    };
    const formDescriptorField: RimsFormDescriptorField = {
      name: rimsStepPropertyTree.name,
      displayName: rimsStepPropertyTree.displayName,
      description: rimsStepPropertyTree.description,
      value: rimsStepPropertyTree.value as string,
      fieldType: this.getFormFieldType(rimsStepPropertyTree.schema),
      rawFieldData: rimsStepPropertyTree,
      parentGroup: null,
    };

    const propertyType = getPropertyType(rimsStepPropertyTree.schema);

    let dateValue: string | null = null;
    if (rimsStepPropertyTree.schema.format === 'date') {
      dateValue = rimsStepPropertyTree.value as string;
      formDescriptor.renderFields = [formDescriptorField];
    } else if (rimsStepPropertyTree.schema.enum) {
      formDescriptorField.options = [...rimsStepPropertyTree.schema.enum];
      formDescriptor.renderFields = [formDescriptorField];
    } else if (propertyType === 'object') {
      formDescriptor.renderFields = this.mapObjectChildrenToFormDescriptorFields(
        rimsStepPropertyTree.children!,
        formDescriptor,
      );
    } else if (
      propertyType === 'boolean' ||
      propertyType === 'number' ||
      propertyType === 'integer' ||
      propertyType === 'string'
    ) {
      formDescriptor.renderFields = [formDescriptorField];
    }

    formDescriptor.formGroup.addControl(
      rimsStepPropertyTree.name,
      new FormControl(createDate(dateValue) ?? rimsStepPropertyTree.value),
      {
        emitEvent: false,
      },
    );

    this.registerForm(formDescriptor);

    return formDescriptor;
  }

  public formExists(rimsStepPropertyTree: RimsStepPropertyTree): boolean {
    return this.formTracker.has(rimsStepPropertyTree);
  }

  public getFormId(rimsStepPropertyTree: RimsStepPropertyTree): string | undefined {
    return this.formTracker.get(rimsStepPropertyTree);
  }

  public getTouchedForms(): RimsFormDescriptor[] {
    const forms = this.formCollectionsSignal();
    return Array.from(forms.entries())
      .map(([, formDescriptor]) => {
        const { formGroup } = formDescriptor;

        if (!formGroup.touched) {
          return;
        }

        return formDescriptor;
      })
      .filter((fd) => !!fd);
  }

  public setActiveForm(form: RimsFormDescriptor): void {
    this.currentForm.update(() => {
      return form;
    });
  }

  public setActiveFormById(formId: string): void {
    const collections = this.formCollectionsSignal();

    this.setActiveForm(collections.get(formId)!);
  }

  public clearActiveForm(): void {
    this.currentForm.set(null);
  }

  private registerForm(incomingForm: RimsFormDescriptor): string {
    const { formHash, formName, formRawData } = incomingForm;

    this.formCollectionsSignal.update((collection) => {
      if (!formHash) {
        throw new Error(`Form ${formName} has no hash!`);
      }

      collection.set(formHash, incomingForm);
      return collection;
    });

    this.formTracker.set(formRawData, formHash);

    return formHash;
  }

  private getFormFieldType(rimsStepPropertySchema: RimsStepPropertySchema): RimsFormDescriptorField['fieldType'] {
    if (rimsStepPropertySchema.enum) {
      return 'dropdown';
    } else if (rimsStepPropertySchema.format === 'date') {
      return 'datepicker';
    }

    switch (getPropertyType(rimsStepPropertySchema)) {
      case 'string':
        return 'input-text';
      case 'number':
      case 'integer':
        return 'input-number';
      case 'boolean':
        return 'checkbox';
      case 'array':
        return 'form-redirect';
      case 'object':
        return 'form-redirect';
      default:
        return 'unknown';
    }
  }

  private mapObjectChildrenToFormDescriptorFields(
    objectChildren: RimsStepPropertyTree[],
    formDescriptor: RimsFormDescriptor,
  ): RimsFormDescriptorField[] {
    return objectChildren.map((objectChild) => {
      let parsedDate: string | null = null;
      if (objectChild.schema.format === 'date') {
        parsedDate = objectChild.value as string;
      }

      const renderField: RimsFormDescriptorField = {
        name: objectChild.name,
        displayName: objectChild.displayName,
        description: objectChild.description,
        value: objectChild.value as string,
        fieldType: this.getFormFieldType(objectChild.schema),
        rawFieldData: objectChild,
        parentGroup: null,
        options: objectChild.schema.enum ?? [],
      };

      formDescriptor.formGroup.addControl(
        renderField.name,
        new FormControl(createDate(parsedDate) ?? renderField.value),
        {
          emitEvent: false,
        },
      );

      return renderField;
    });
  }
}
